import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLookup } from "@/components/shared";
import SignaturePad from "@/components/SignaturePad";
import { fmtDate } from "@/lib/date";
import { X, Trash, ArrowsClockwise, Plus, CheckCircle } from "@phosphor-icons/react";

const inp = "w-full h-11 px-3 border border-border-control bg-white text-[15px] rounded-[9px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none disabled:bg-surface-panel disabled:text-ink-tertiary";
const smInp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none disabled:bg-surface-panel disabled:text-ink-tertiary";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-muted mb-1.5 block";
const cardLbl = "text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-muted";
const SafetyKinds = ["Observation", "Incident", "Near Miss"];

function CountChip({ n, tone = "warn" }) {
  const c = tone === "warn" ? "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]" : "bg-surface-inset text-ink-secondary border-border";
  return <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-[6px] border ${c}`}>{n}</span>;
}

export default function SiteDiaryModal({ projectId, date: initialDate, projectName, onClose }) {
  const { isWriter } = useAuth();
  const delayCauses = useLookup("standdown_reasons");
  const [date, setDate] = useState(initialDate || new Date().toISOString().slice(0, 10));
  const [diary, setDiary] = useState(null);
  const [plantAuto, setPlantAuto] = useState([]);
  const [personnelAuto, setPersonnelAuto] = useState([]);
  const [meta, setMeta] = useState({ name: projectName || "", ref: "", client: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [signName, setSignName] = useState("");
  const [signSig, setSignSig] = useState("");

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}`).then(({ data }) =>
      setMeta({ name: data.project_name || data.site_address || "", ref: data.project_no || "", client: data.client || "" })).catch(() => {});
  }, [projectId]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError(""); setMsg("");
    try {
      const { data } = await api.get(`/projects/${projectId}/site-diary`, { params: { date } });
      setDiary(data);
      setPlantAuto(data.plant_auto || []);
      setPersonnelAuto(data.personnel_auto || []);
      setSignName(data.signoff?.name || "");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setLoading(false);
  }, [projectId, date]);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setDiary((d) => ({ ...d, ...patch }));
  const setWeather = (k, v) => setDiary((d) => ({ ...d, weather: { ...(d.weather || {}), [k]: v } }));
  const setRow = (key, i, k, v) => setDiary((d) => { const arr = [...(d[key] || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...d, [key]: arr }; });
  const addRow = (key, blank) => setDiary((d) => ({ ...d, [key]: [...(d[key] || []), blank] }));
  const delRow = (key, i) => setDiary((d) => ({ ...d, [key]: (d[key] || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setError(""); setMsg("");
    try {
      const { weather, personnel, plant, activities, delays, safety } = diary;
      await api.put(`/projects/${projectId}/site-diary`, { weather, personnel, plant, activities, delays, safety }, { params: { date } });
      setMsg("Saved."); setTimeout(() => setMsg(""), 2500); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sign = async () => {
    setError("");
    if (!signName) { setError("Enter the supervisor's name to sign off."); return; }
    try {
      const { weather, personnel, plant, activities, delays, safety } = diary;
      await api.put(`/projects/${projectId}/site-diary`, { weather, personnel, plant, activities, delays, safety }, { params: { date } });
      await api.post(`/projects/${projectId}/site-diary/sign`, { name: signName, image: signSig }, { params: { date } });
      setMsg("Signed off."); load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const w = diary?.weather || {};
  const signed = diary?.status === "signed";
  const ro = !isWriter;
  const personnelCount = (diary?.personnel || []).length;

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-card border border-border-warm rounded-[18px] overflow-hidden shadow-modal w-full max-w-[680px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="sitediary-modal">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Site Diary</div>
              <h3 className="text-[26px] font-bold text-ink-primary leading-tight mt-0.5">{fmtDate(date)}</h3>
              <p className="text-[13.5px] text-ink-secondary mt-1 truncate">
                {meta.name}{meta.ref ? <span className="font-mono text-ink-tertiary"> #{meta.ref}</span> : null}{meta.client ? ` · ${meta.client}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span data-testid="sitediary-status"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] rounded-full border ${signed ? "bg-[#E4F2E4] text-[#256428] border-[#BFE0C0]" : "bg-surface-inset text-ink-secondary border-border"}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: signed ? "#2E7D32" : "#8A857A" }} />
                {signed ? `Signed · ${diary.signoff?.name || "Supervisor"}` : "Draft"}
              </span>
              <button onClick={onClose} data-testid="sitediary-modal-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-auto space-y-5 bg-surface-inset">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="diary-error">{error}</div>}
          {msg && <div className="border border-green-300 bg-green-50 text-green-700 text-sm px-3 py-2 rounded-[8px]" data-testid="diary-msg">{msg}</div>}

          {loading || !diary ? <div className="py-10 text-center text-ink-tertiary text-sm">Loading…</div> : (
            <>
              {/* Date + weather */}
              <div className="grid grid-cols-3 gap-4">
                <div><label className={lbl}>Date</label><input type="date" className={inp} value={date} onChange={(e) => setDate(e.target.value)} data-testid="diary-date" /></div>
                <div><label className={lbl}>Weather AM</label><input disabled={ro} className={inp} value={w.am || ""} onChange={(e) => setWeather("am", e.target.value)} placeholder="Fine / Overcast…" data-testid="diary-weather-am" /></div>
                <div><label className={lbl}>Weather PM</label><input disabled={ro} className={inp} value={w.pm || ""} onChange={(e) => setWeather("pm", e.target.value)} placeholder="Showers…" data-testid="diary-weather-pm" /></div>
              </div>

              {/* Rain + personnel */}
              <div className="grid grid-cols-3 gap-4">
                <div><label className={lbl}>Rain (mm)</label><input disabled={ro} type="number" step="0.5" className={inp + " text-right"} value={w.rain_mm ?? ""} onChange={(e) => setWeather("rain_mm", e.target.value === "" ? null : Number(e.target.value))} data-testid="diary-weather-rain" /></div>
                <div className="col-span-2">
                  <label className={lbl}>Personnel on site</label>
                  <div className="flex items-center gap-2 h-11 px-3 border border-border-control bg-white rounded-[9px]">
                    <span className="text-[16px] font-bold text-ink-primary" data-testid="diary-personnel-count">{personnelCount}</span>
                    <span className="text-[14px] text-ink-secondary flex-1">worker{personnelCount === 1 ? "" : "s"} checked in on this date</span>
                    {!ro && <button onClick={() => set({ personnel: personnelAuto.map((p) => ({ ...p })) })} data-testid="diary-reset-personnel" className="text-[13px] font-bold text-accent-ink hover:underline shrink-0">From allocations</button>}
                  </div>
                </div>
              </div>

              {/* Plant on site */}
              <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-divider-inner">
                  <h4 className="font-bold text-[15px] text-ink-primary">Plant on site <span className="font-mono text-[12px] font-normal text-ink-tertiary ml-1">{(diary.plant || []).length} units</span></h4>
                  {!ro && <button onClick={() => set({ plant: plantAuto.map((p) => ({ ...p })) })} data-testid="diary-reset-plant" className="text-[13px] font-bold text-accent-ink hover:underline inline-flex items-center gap-1"><ArrowsClockwise size={13} /> From allocations</button>}
                </div>
                {(diary.plant || []).length === 0 ? <p className="px-4 py-4 text-[13px] text-ink-tertiary">No plant on site for this date.</p> : (
                  <div>
                    {(diary.plant || []).map((r, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-3 items-center px-4 py-2.5 border-b border-divider-row last:border-0">
                        <input disabled={ro} className={smInp + " font-mono font-semibold"} placeholder="Asset #" value={r.asset_no || ""} onChange={(e) => setRow("plant", i, "asset_no", e.target.value)} data-testid={`diary-plant-asset-${i}`} />
                        <input disabled={ro} className={smInp} placeholder="Type" value={r.asset_type || ""} onChange={(e) => setRow("plant", i, "asset_type", e.target.value)} />
                        <input disabled={ro} className={smInp} placeholder="Supplier" value={r.supplier || ""} onChange={(e) => setRow("plant", i, "supplier", e.target.value)} />
                        {!ro ? <button onClick={() => delRow("plant", i)} className="w-9 h-9 flex items-center justify-center rounded-[7px] border border-border-control text-ink-tertiary hover:text-red-600 hover:border-red-300 justify-self-center"><Trash size={15} /></button> : <span />}
                      </div>
                    ))}
                  </div>
                )}
                {!ro && <div className="px-4 py-2.5 border-t border-divider-inner"><button onClick={() => addRow("plant", { asset_no: "", asset_type: "", supplier: "" })} data-testid="diary-add-plant" className="text-[13px] font-bold text-accent-ink hover:underline">Add plant</button></div>}
              </section>

              {/* Activities */}
              <div>
                <label className={lbl}>Activities / Progress</label>
                <textarea disabled={ro} className={inp + " h-24 py-2"} value={diary.activities || ""} onChange={(e) => set({ activities: e.target.value })} placeholder="Work completed, quantities achieved…" data-testid="diary-activities" />
              </div>

              {/* Delays + Safety */}
              <div className="grid grid-cols-2 gap-4">
                <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-divider-inner">
                    <h4 className="font-bold text-[15px] text-ink-primary inline-flex items-center">Delays <CountChip n={(diary.delays || []).length} /></h4>
                    {!ro && <button onClick={() => addRow("delays", { cause: "", description: "", hours: null })} data-testid="diary-add-delay" className="text-[13px] font-bold text-accent-ink hover:underline">Add</button>}
                  </div>
                  <div className="p-4 space-y-2">
                    {(diary.delays || []).length === 0 ? <p className="text-[13px] text-ink-tertiary">No delays recorded.</p> :
                      (diary.delays || []).map((r, i) => (
                        <div key={i} className="space-y-1.5">
                          {!ro ? (
                            <>
                              <div className="flex gap-2">
                                <select disabled={ro} className={smInp} value={r.cause || ""} onChange={(e) => setRow("delays", i, "cause", e.target.value)} data-testid={`diary-delay-cause-${i}`}>
                                  <option value="">Cause…</option>
                                  {r.cause && !delayCauses.includes(r.cause) && <option value={r.cause}>{r.cause}</option>}
                                  {delayCauses.map((c) => <option key={c}>{c}</option>)}
                                </select>
                                <input disabled={ro} type="number" step="0.5" className={smInp + " w-20 shrink-0"} placeholder="hrs" value={r.hours ?? ""} onChange={(e) => setRow("delays", i, "hours", e.target.value === "" ? null : Number(e.target.value))} />
                                <button onClick={() => delRow("delays", i)} className="text-ink-tertiary hover:text-red-600 shrink-0"><Trash size={15} /></button>
                              </div>
                              <input disabled={ro} className={smInp} placeholder="Description" value={r.description || ""} onChange={(e) => setRow("delays", i, "description", e.target.value)} />
                            </>
                          ) : <p className="text-[14px] text-ink-secondary">{r.description || r.cause || "—"}{r.hours != null ? ` (${r.hours}h)` : ""}</p>}
                        </div>
                      ))}
                  </div>
                </section>

                <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-divider-inner">
                    <h4 className="font-bold text-[15px] text-ink-primary inline-flex items-center">Safety <CountChip n={(diary.safety || []).length} tone={(diary.safety || []).some((s) => s.kind === "Incident") ? "warn" : "warn"} /></h4>
                    {!ro && <button onClick={() => addRow("safety", { kind: "Observation", description: "" })} data-testid="diary-add-safety" className="text-[13px] font-bold text-accent-ink hover:underline">Add</button>}
                  </div>
                  <div className="p-4 space-y-2">
                    {(diary.safety || []).length === 0 ? <p className="text-[13px] text-ink-tertiary">No safety items recorded.</p> :
                      (diary.safety || []).map((r, i) => (
                        <div key={i} className="space-y-1.5">
                          {!ro ? (
                            <>
                              <select disabled={ro} className={smInp} value={r.kind || "Observation"} onChange={(e) => setRow("safety", i, "kind", e.target.value)} data-testid={`diary-safety-kind-${i}`}>
                                {SafetyKinds.map((k) => <option key={k}>{k}</option>)}
                              </select>
                              <div className="flex gap-2">
                                <input disabled={ro} className={smInp} placeholder="Description" value={r.description || ""} onChange={(e) => setRow("safety", i, "description", e.target.value)} />
                                <button onClick={() => delRow("safety", i)} className="text-ink-tertiary hover:text-red-600 shrink-0"><Trash size={15} /></button>
                              </div>
                            </>
                          ) : <p className="text-[14px] text-ink-secondary"><span className="font-semibold">{r.kind}:</span> {r.description || "—"}</p>}
                        </div>
                      ))}
                  </div>
                </section>
              </div>

              {/* Supervisor sign-off */}
              {isWriter && (
                <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden">
                  <div className="px-4 py-3 border-b border-divider-inner"><h4 className="font-bold text-[15px] text-ink-primary">Supervisor sign-off</h4></div>
                  <div className="p-4 space-y-3">
                    <div><label className={lbl}>Supervisor name</label><input className={inp} placeholder="Supervisor name" value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="diary-sign-name" /></div>
                    <div className="bg-white border border-border-control rounded-[9px] p-2">
                      <SignaturePad onChange={setSignSig} testid="diary-sign-sig" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12.5px] text-ink-tertiary">{signed ? `Signed ${fmtDate(date)}` : "Not signed yet"}</span>
                      <button onClick={() => setSignSig("")} className="text-[13px] font-bold text-accent-ink hover:underline">Clear</button>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {isWriter && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border bg-surface-panel shrink-0">
            <p className="text-[12.5px] text-ink-tertiary max-w-[52%] leading-snug">Signed diaries are part of the project record. Re-signing replaces the existing signature and notifies the project manager.</p>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={save} data-testid="diary-save" className="h-11 px-5 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] hover:bg-surface-panel active:bg-surface-pressed">Save Draft</button>
              <button onClick={sign} data-testid="diary-sign-btn" className="h-11 px-6 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
                {signed ? "Re-sign Diary" : "Sign Diary"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
